import { AGENT_INTERCEPTION_IGNORED_SELECTORS } from '../constants';

/**
 * Bare modifier presses. A modifier keydown fires on its OWN, before the combo it
 * begins — pressing `Alt+I` emits `keydown{key:'Alt'}` and only then
 * `keydown{key:'i', altKey:true}`.
 *
 * So a guard that treats any keydown as taking over the order fires on the Alt, and
 * the shortcut's own `stopPropagation()` never gets the chance to prevent it. Every
 * Alt shortcut in the app is affected — `Alt+1…9` intents, `Alt+A`/`Alt+D`,
 * `Alt+/`, and `Alt+I` — which is why this belongs in the shared guard and not in
 * any one hotkey.
 *
 * Holding a modifier is not an edit. The keystroke it prefixes may well be, and that
 * one still arrives and is judged on its own.
 */
const MODIFIER_KEYS = ['Alt', 'Control', 'Shift', 'Meta', 'AltGraph'];

/**
 * True when an event must NOT raise the "would you like to take over?" prompt.
 *
 * Shared by AgentInterceptionConfirm (HITL 3.0) and AIStatusBar (legacy) so the two
 * cannot drift — their dialog logic is documented as kept in lockstep, and it was
 * previously copy-pasted.
 *
 * Two reasons to ignore an event:
 * 1. It is a bare modifier keydown (above).
 * 2. Its target sits inside an allowlisted subtree — chrome, the transcript, the
 *    issue-report panel. Matched with `closest()`, so the marker need only be an
 *    ANCESTOR of the real target.
 */
export const isInterceptionIgnoredEvent = (event: Event): boolean => {
  if (event.type === 'keydown' && MODIFIER_KEYS.includes((event as KeyboardEvent).key)) {
    return true;
  }
  const target = event.target as HTMLElement | null;
  return Boolean(
    target &&
      typeof target.closest === 'function' &&
      AGENT_INTERCEPTION_IGNORED_SELECTORS.some((selector) =>
        target.closest(selector)
      )
  );
};

export default isInterceptionIgnoredEvent;
